import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  Link2,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  BedDouble,
} from "lucide-react";
import { fadeUp } from "../../../../utils/motion";

/* ================= HELPERS ================= */

function extractListingId(text) {
  if (!text) return null;
  const match = text.match(/airbnb.*rooms\/(\d+)/i);
  return match ? match[1] : null;
}

function isValidAirbnbUrl(text) {
  try {
    const url = new URL(text);
    return url.hostname.includes("airbnb");
  } catch {
    return false;
  }
}

/* 🔥 MOCK FETCH (replace later with real API) */
async function fetchListingData(id) {
  await new Promise((r) => setTimeout(r, 500));

  return {
    title: "Modern Apartment with City View",
    image:
      "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688",
    beds: 2,
  };
}

/* ================= COMPONENT ================= */

export default function StepAirbnbLink({ onNext, onChange }) {
  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const listingId = extractListingId(value);
  const valid = isValidAirbnbUrl(value) && listingId;

  /* ================= FETCH PREVIEW ================= */

  useEffect(() => {
    if (!valid) {
      setPreview(null);
      return;
    }

    let active = true;
    setLoadingPreview(true);

    fetchListingData(listingId).then((data) => {
      if (!active) return;

      setPreview(data);
      setLoadingPreview(false);

      // 🔥 auto-fill beds
      onChange?.({ beds: data.beds });
    });

    return () => (active = false);
  }, [listingId, valid]);

  /* ================= SUBMIT ================= */

  function handleSubmit() {
    if (!valid) return;

    onChange?.({
      airbnbUrl: value,
      listingId,
    });

    onNext?.();
  }

  return (
    <div className="max-w-content space-y-8">

      {/* HEADER */}
      <motion.div variants={fadeUp}>
        <h2 className="text-title font-extrabold text-text-primary">
          Analyse your Airbnb listing
        </h2>

        <p className="mt-2 text-body text-text-muted">
          Paste your listing link to unlock pricing, demand and performance insights.
        </p>
      </motion.div>

      {/* INPUT */}
      <motion.div
        variants={fadeUp}
        className={`
          rounded-[22px] border bg-bg-surface p-5 md:p-6
          ${valid ? "border-accent-gold" : "border-border-soft"}
        `}
      >
        <div className="flex items-center gap-3">

          <div className={`
            flex h-10 w-10 items-center justify-center rounded-xl border
            ${valid
              ? "border-accent-gold bg-accent-gold text-black"
              : "border-border-soft bg-bg-light text-text-muted"}
          `}>
            <Link2 size={18} />
          </div>

          <input
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setTouched(true);
            }}
            placeholder="Paste Airbnb listing URL"
            className="flex-1 bg-transparent outline-none text-text-primary"
          />

        </div>

        {/* STATUS */}
        {touched && (
          <div className="mt-3 flex items-center gap-2 text-xs">

            {valid ? (
              <>
                <CheckCircle size={14} className="text-accent-gold" />
                <span className="text-accent-gold">
                  Listing detected • ID: {listingId}
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={14} className="text-red-400" />
                <span className="text-red-400">
                  Enter a valid Airbnb URL
                </span>
              </>
            )}

          </div>
        )}
      </motion.div>

      {/* PREVIEW */}
      {valid && (
        <motion.div
          variants={fadeUp}
          className="
            rounded-2xl border border-border-soft
            bg-bg-surface overflow-hidden
          "
        >

          {loadingPreview ? (
            <div className="p-6 text-small text-text-muted">
              Fetching listing details...
            </div>
          ) : preview && (
            <div className="flex gap-4 p-4">

              <img
                src={preview.image}
                className="w-24 h-20 object-cover rounded-lg"
              />

              <div className="flex-1">
                <div className="font-semibold text-text-primary">
                  {preview.title}
                </div>

                <div className="mt-1 flex items-center gap-2 text-small text-text-muted">
                  <BedDouble size={14} />
                  {preview.beds} bedrooms detected
                </div>

                <a
                  href={value}
                  target="_blank"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-accent-gold"
                >
                  View listing <ExternalLink size={12} />
                </a>
              </div>

            </div>
          )}

        </motion.div>
      )}

      {/* CTA */}
      <motion.button
        variants={fadeUp}
        onClick={handleSubmit}
        disabled={!valid}
        className="
          w-full py-3 rounded-pill
          bg-brand-primary text-white font-semibold
          hover:opacity-90 active:scale-[0.98]
          disabled:opacity-40
        "
      >
        Analyse listing
      </motion.button>

    </div>
  );
}